import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, Plus, X } from "lucide-react";

const KEY = "vixera_ios_install_seen";

function isIOS() {
  const ua = navigator.userAgent || "";
  const isiOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|Chrome/.test(ua);
  return isiOS && isSafari;
}

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
}

/**
 * Bottom-anchored toast on iOS Safari that walks the user through
 * "Add to Home Screen". Shown once, dismissible. Not shown once the site
 * is already installed (standalone mode).
 */
export default function IOSInstallHint() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (!isIOS()) return;
    if (localStorage.getItem(KEY) === "1") return;
    const t = setTimeout(() => setVisible(true), 3500);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(KEY, "1");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="ios-hint"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          data-testid="ios-install-hint"
          className="fixed inset-x-4 bottom-4 z-[110] border border-velvet-gold/40 bg-velvet-coal/95 p-4 shadow-2xl backdrop-blur-md sm:inset-x-auto sm:right-6 sm:max-w-sm"
        >
          <div className="flex items-start gap-3">
            <img src="/icon-192.png" alt="" className="h-12 w-12 flex-shrink-0 rounded-xl" />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-luxe text-velvet-gold">
                Install Vixera
              </p>
              <p className="mt-1 text-sm text-white">Add to Home Screen for the full app experience.</p>
              <p className="mt-2 flex items-center gap-1.5 text-xs font-light text-white/60">
                Tap the <Share2 size={13} className="inline text-velvet-gold" /> Share icon, then
                <span className="mx-1 whitespace-nowrap rounded border border-white/15 px-2 py-0.5 text-[10px] uppercase tracking-luxe text-white">
                  <Plus size={10} className="mr-0.5 inline" /> Add to Home Screen
                </span>
              </p>
            </div>
            <button
              onClick={dismiss}
              aria-label="Dismiss"
              data-testid="ios-install-dismiss"
              className="p-1 text-white/40 hover:text-white"
            >
              <X size={16} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
